import {
  ButtonDS,
  DialogModalDS,
  TextCapitalized,
  Spacer,
  TableDS,
  Tag,
  MessageTooltip,
  LoaderOverlay,
} from "@qivia/ui";
import { selectCompany } from "../../homeSlice";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { useTranslation } from "react-i18next";
import { useMemo, useState, useCallback, useEffect } from "react";
import {
  requestVehiclesConnectionAsync,
  selectListAllVehiclesInDb,
  selectRequestVehiclesConnectionStatus,
} from "../vehiclesSlice";
import { VehiclesListType } from "../vehiclesAPI";
import {
  DATA_PROVIDER_BY_CONNECTION_TYPE,
  VehicleConnectionType,
  findVehicleConnectionType,
} from "../vehicleConnection";
import { EmptyTable } from "@qivia/ui/src/assets/assets";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import Car from "@qivia/ui/src/designSystem/assets/Car.png";

type VehicleConnectionRow = Pick<
  VehiclesListType,
  "reference" | "registrationNumber"
> & { connectionType: VehicleConnectionType };

export const AddMultipleVehiclesRequestConnectionModal = (props: {
  visible: boolean;
  onCloseModal: () => void;
}) => {
  const { t } = useTranslation();
  const { onCloseModal, visible } = props;
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const vehiclesListInDb = useAppSelector(selectListAllVehiclesInDb);
  const requestVehiclesConnectionStatus = useAppSelector(
    selectRequestVehiclesConnectionStatus,
  );

  const [selectedRows, setSelectedRows] = useState<VehicleConnectionRow[]>([]);
  const [displayError, setDisplayError] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);

  const vehiclesNotConnected = useMemo(
    () =>
      vehiclesListInDb.filter(
        (vehicle) =>
          vehicle.connectionStatus === "IDLE" ||
          vehicle.connectionStatus === "REVOKED",
      ),
    [vehiclesListInDb],
  );

  const vehicleRows = useMemo<VehicleConnectionRow[]>(
    () =>
      vehiclesNotConnected.map((v) => ({
        reference: v.reference,
        registrationNumber: v.registrationNumber,
        connectionType: findVehicleConnectionType(v),
      })),
    [vehiclesNotConnected],
  );

  const handleCloseModal = useCallback(() => {
    setSelectedRows([]);
    setDisplayError(false);
    setLoader(false);
    onCloseModal();
  }, [onCloseModal]);

  const handleSetSelectedRows = useCallback((rows: VehicleConnectionRow[]) => {
    if (rows.length > 0) setDisplayError(false);
    setSelectedRows(rows);
  }, []);

  const isValidForm = useMemo(
    () => selectedRows.length > 0 && company !== null,
    [selectedRows, company],
  );

  const noVehiclesSelectedError: string | undefined = useMemo(
    () =>
      displayError && selectedRows.length === 0
        ? t("vehicles.modal.requestConnection.error.noVehiclesSelected")
        : undefined,
    [displayError, selectedRows, t],
  );

  const handleSubmitVehiclesRequestConnection = useCallback(() => {
    setDisplayError(true);

    if (!company || !isValidForm) return;

    const selectedRegistrationNumbers = selectedRows.map(
      (row) => row.registrationNumber,
    );
    const vehicles = vehiclesNotConnected
      .filter((vehicle) =>
        selectedRegistrationNumbers.includes(vehicle.registrationNumber),
      )
      .map((vehicle) => ({
        registrationNumber: vehicle.registrationNumber,
        uuid: vehicle.uuid,
        dataProvider:
          DATA_PROVIDER_BY_CONNECTION_TYPE[findVehicleConnectionType(vehicle)],
        brand: vehicle.brand ?? null,
        dateMec: vehicle.dateMec ?? null,
      }));

    void dispatch(
      requestVehiclesConnectionAsync({
        companyUuid: company.uuid,
        companyName: company.name,
        connectionPlan: "LIGHT",
        vehicles,
      }),
    );

    setLoader(true);
  }, [company, isValidForm, dispatch, selectedRows, vehiclesNotConnected]);

  useEffect(() => {
    if (requestVehiclesConnectionStatus === "success") {
      handleCloseModal();
    }
  }, [requestVehiclesConnectionStatus, handleCloseModal]);

  const render =
    (row: VehicleConnectionRow) => (key: keyof VehicleConnectionRow) => {
      switch (key) {
        case "reference":
          return (
            <StyledReference>
              <StyledCar>
                <StyledCarImage src={Car} alt="Car" />
              </StyledCar>
              <Spacer x={1} />
              {row[key] && (
                <Tag
                  textColor={colors["colors/text/black"]}
                  backgroundColor={
                    colors["colors/surfaces/background/background_level0"]
                  }
                  text={row[key]}
                  borderColor={colors["colors/borders/cells/cells"]}
                />
              )}
            </StyledReference>
          );
        case "registrationNumber":
          return row.registrationNumber ?? "-";
        case "connectionType":
          return (
            <StyledConnectionType>
              <Tag
                textColor={colors["colors/text/darkGrey"]}
                text={t(`vehicles.connectionType.${row.connectionType}`)}
                backgroundColor={
                  colors["colors/surfaces/background/background_level1"]
                }
              />
            </StyledConnectionType>
          );
      }
    };

  const headers = {
    reference: {
      text: t("vehicles.modal.requestConnection.table.header.reference"),
    },
    registrationNumber: {
      text: t(
        "vehicles.modal.requestConnection.table.header.registrationNumber",
      ),
    },
    connectionType: {
      text: t("vehicles.modal.requestConnection.table.header.connectionType"),
    },
  };

  return (
    <DialogModalDS
      visible={visible}
      width={44}
      onClose={handleCloseModal}
      deactivateClickOutsideSidePanel={loader}
      title={t("vehicles.modal.requestConnection.title") || ""}
      subTitle={t("vehicles.modal.requestConnection.subTitle") || ""}
      bottomButtons={{
        leftButton: (
          <ButtonDS
            text={t("vehicles.modal.cancel")}
            format={"fill"}
            buttonType={"secondary"}
            onClick={handleCloseModal}
          />
        ),
        rightButton: (
          <ButtonDS
            text={t("vehicles.modal.requestConnection")}
            format={"fill"}
            buttonType={"primary"}
            onClick={handleSubmitVehiclesRequestConnection}
            disabled={!isValidForm || loader}
          />
        ),
      }}
    >
      <StyledRequestModalContainer>
        <LoaderOverlay visible={loader} />
        <TableDS<keyof VehicleConnectionRow, VehicleConnectionRow>
          data={vehicleRows}
          headers={headers}
          render={render}
          height={25}
          rowsChecked={selectedRows}
          setRowsChecked={handleSetSelectedRows}
          header-checkbox-data-cy="header-checkbox-data-cy"
          headerOverlayed={
            <StyledHeader>
              {t("vehicles.modal.requestConnection.table.header", {
                number: selectedRows.length,
              })}
            </StyledHeader>
          }
          emptyContent={
            <StyledFlex>
              <Spacer x={1.5} />
              <StyledEmptyTable>
                <Spacer y={2} />
                <EmptyTable />
                <Spacer y={1.5} />
                <StyledTitleEmptyTable>
                  <TextCapitalized>
                    {t("vehicles.modal.requestConnection.listEmpty.title")}
                  </TextCapitalized>
                </StyledTitleEmptyTable>
                <Spacer y={1} />
                <StyledSubText>
                  <TextCapitalized>
                    {t("vehicles.modal.requestConnection.listEmpty.body")}
                  </TextCapitalized>
                </StyledSubText>
                <Spacer y={2} />
              </StyledEmptyTable>
            </StyledFlex>
          }
        />
        <Spacer y={1} />
        <MessageTooltip
          title={t("vehicles.modal.requestConnection.tooltip.title")}
          text={t("vehicles.modal.requestConnection.tooltip.message")}
          type="INFORMATIVE"
        />
        {noVehiclesSelectedError && (
          <>
            <Spacer y={1} />
            <StyledError>
              <TextCapitalized>{noVehiclesSelectedError}</TextCapitalized>
            </StyledError>
          </>
        )}
      </StyledRequestModalContainer>
    </DialogModalDS>
  );
};

const StyledRequestModalContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledReference = styled.div`
  display: flex;
  align-items: center;
`;

const StyledCar = styled.div`
  display: flex;
  width: 2rem;
`;

const StyledCarImage = styled.img`
  width: 100%;
  filter: grayscale(100%);
`;

const StyledEmptyTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  text-align: center;
`;

const StyledFlex = styled.div`
  display: flex;
`;

const StyledTitleEmptyTable = styled.div`
  display: flex;
  ${typographies["Header/H1"]};
  color: ${colors["colors/text/black"]};
`;

const StyledHeader = styled.div`
  display: flex;
  ${typographies["Header/H3"]};
  color: ${colors["colors/text/black"]};
  align-items: center;
`;

const StyledSubText = styled.div`
  display: flex;
  ${typographies["Body/S"]};
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledConnectionType = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledError = styled.div`
  ${typographies["Body/S"]};
  color: ${colors["colors/system/error/error_normal"]};
`;
